import { useEffect, useMemo, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import {
  Bell,
  Command,
  FileText,
  LayoutDashboard,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Sun,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Home from "@/pages/Home";
import Tools from "@/pages/Tools";
import Editor from "@/pages/Editor";
import Settings from "@/pages/Settings";
import NotFound from "@/pages/NotFound";
import { loadRecentSessions, type StoredSession } from "@/lib/session-store";
import { applyPrefs } from "@/lib/prefs";
import ErrorBoundary from "@/components/ErrorBoundary";

const navigation = [
  { label: "Workspace", href: "/", icon: LayoutDashboard },
  { label: "All tools", href: "/tools", icon: Wrench },
  { label: "PDF editor", href: "/editor", icon: FileText },
];

function AppShell({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [notice, setNotice] = useState("");
  const [recentSessions, setRecentSessions] = useState<StoredSession[]>([]);

  useEffect(() => {
    applyPrefs();
    void loadRecentSessions().then(setRecentSessions).catch(() => undefined);
  }, [location]);

  const pageTitle = useMemo(() => {
    if (location === "/tools") return "All tools";
    if (location === "/editor") return "PDF editor";
    if (location === "/settings") return "Settings";
    return "Workspace";
  }, [location]);

  const notify = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2800);
  };

  return (
    <div className={darkMode ? "app-shell dark-shell" : "app-shell light-shell"}>
      <aside className={collapsed ? "sidebar sidebar-collapsed" : "sidebar"}>
        <div className="brand-row">
          <button className="brand-mark" onClick={() => setLocation("/")} aria-label="Go to workspace">
            <span className="brand-mark-inner">L</span>
          </button>
          {!collapsed && (
            <button className="brand-wordmark" onClick={() => setLocation("/")}>
              local<span>pdf</span>
            </button>
          )}
          <button className="sidebar-toggle" onClick={() => setCollapsed((v) => !v)} aria-label="Toggle sidebar">
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        {!collapsed && <div className="eyebrow sidebar-eyebrow">Private by design</div>}
        <nav className="sidebar-nav" aria-label="Primary navigation">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = location === item.href;
            return (
              <button
                key={item.href}
                className={active ? "nav-item active" : "nav-item"}
                onClick={() => setLocation(item.href)}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
                {!collapsed && <span>{item.label}</span>}
                {active && !collapsed && <span className="nav-dot" />}
              </button>
            );
          })}
        </nav>

        {!collapsed && (
          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <span className="eyebrow">Recent</span>
              <button
                className="icon-button subtle"
                onClick={() => notify("Recent files are stored only in this browser.")}
                aria-label="Recent file details"
              >
                <SlidersHorizontal size={14} />
              </button>
            </div>
            <div className="recent-list">
              {recentSessions.length ? (
                recentSessions.slice(0, 3).map((session) => (
                  <button
                    className="recent-item"
                    key={session.id}
                    onClick={() => setLocation(`/editor?session=${encodeURIComponent(session.id)}`)}
                  >
                    <div className="recent-file-icon">
                      <FileText size={14} />
                    </div>
                    <div>
                      <strong>{session.fileName}</strong>
                      <span>{session.pageCount} pages · saved locally</span>
                    </div>
                  </button>
                ))
              ) : (
                <button className="recent-item" onClick={() => notify("Open a PDF to create a browser-local session.")}>
                  <div className="recent-file-icon">
                    <FileText size={14} />
                  </div>
                  <div>
                    <strong>No saved PDF yet</strong>
                    <span>Files stay in this browser</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="sidebar-bottom">
          {!collapsed && (
            <div className="privacy-card">
              <div className="privacy-icon">
                <ShieldCheck size={16} />
              </div>
              <div>
                <strong>Nothing leaves here.</strong>
                <span>PDF bytes never leave this tab.</span>
              </div>
            </div>
          )}
          <button
            className={location === "/settings" ? "nav-item active" : "nav-item"}
            onClick={() => setLocation("/settings")}
            title={collapsed ? "Settings" : undefined}
          >
            <Settings2 size={17} />
            {!collapsed && <span>Settings</span>}
          </button>
          <div className="profile-row">
            {!collapsed && (
              <>
                <div className="profile-avatar">LP</div>
                <div className="profile-copy">
                  <strong>This browser</strong>
                  <span>No account · local only</span>
                </div>
              </>
            )}
            <button
              className="icon-button subtle profile-more"
              onClick={() => notify("Your workspace is running locally.")}
              aria-label="Workspace details"
            >
              <Command size={15} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main-panel">
        <header className="topbar">
          <div className="topbar-title">
            <span className="eyebrow">Local workspace</span>
            <h1>{pageTitle}</h1>
          </div>
          <div className="topbar-actions">
            <div className="offline-pill">
              <span className="status-dot" /> Browser only
            </div>
            <button
              className="icon-button"
              onClick={() => notify("No notifications — nothing is sent off-device.")}
              aria-label="Notifications"
            >
              <Bell size={17} />
            </button>
            <button className="icon-button" onClick={() => setDarkMode((v) => !v)} aria-label="Toggle theme">
              {darkMode ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <Button className="new-file-button" onClick={() => setLocation("/")}>
              <Plus size={16} /> New file
            </Button>
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>

      <nav className="mobile-dock" aria-label="Mobile navigation">
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = location === item.href;
          return (
            <button
              key={item.href}
              className={active ? "dock-item active" : "dock-item"}
              onClick={() => setLocation(item.href)}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </button>
          );
        })}
        <button
          className={location === "/settings" ? "dock-item active" : "dock-item"}
          onClick={() => setLocation("/settings")}
        >
          <Settings2 size={18} />
          <span>Settings</span>
        </button>
      </nav>

      {notice && (
        <div className="toast-message" role="status">
          <Sparkles size={15} />
          {notice}
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppShell>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/tools" component={Tools} />
          <Route path="/editor" component={Editor} />
          <Route path="/settings" component={Settings} />
          <Route component={NotFound} />
        </Switch>
      </AppShell>
    </ErrorBoundary>
  );
}
